import { parseSplitPayment } from "./splitPaymentReport";

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const firstNumber = (object, keys) => {
  for (const key of keys) {
    if (object?.[key] !== undefined && object?.[key] !== null && object?.[key] !== "") {
      return n(object[key]);
    }
  }
  return 0;
};

const parseObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return null; }
};

const orderItems = (order) => {
  const candidates = [
    order?.summary, order?.itemslist, order?.orderitems, order?.itemlist,
    order?.items, order?.lineitems, order?.line_items, order?.payload?.itemlist,
  ];
  return candidates.find(Array.isArray) || [];
};

const amountOf = (order) => firstNumber(order, [
  "total_amount", "totalamount", "grand_total", "grandtotal", "totalPrice",
  "bill_amount", "billamount", "amount", "total",
]);

const qtyOf = (order) => {
  const direct = firstNumber(order, ["total_quantity", "totalqty", "total_items", "totalquantity", "qty"]);
  if (direct) return direct;
  return orderItems(order).reduce(
    (sum, item) => sum + firstNumber(item, ["total_quantity", "qty", "quantity", "qtyy"]),
    0
  );
};

const normalizePaymentLabel = (value) => {
  const raw = String(value || "Cash").trim();
  const key = raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (key.includes("paytm")) return "Paytm";
  if (key.includes("phonepe")) return "PhonePe";
  if (key.includes("googlepay") || key === "gpay") return "Google Pay";
  if (key.includes("cash")) return "Cash";
  if (key.includes("card")) return "Card";
  if (key.includes("credit") || key.includes("udhar") || key.includes("khata") || key.includes("due")) return "Credit";
  if (key.includes("upi") || key.includes("online")) return "UPI";
  if (key.includes("complimentary") || key.includes("complementary")) return "Complimentary";
  return raw || "Other";
};

const statusOf = (order) => String(
  order?.status ?? order?.order_status ?? order?.orderstatus ?? ""
).trim().toLowerCase();

const isCancelled = (order) =>
  ["cancelled", "canceled", "deleted", "void", "voided"].some((word) => statusOf(order).includes(word)) ||
  order?.is_cancelled === true || Number(order?.is_cancelled) === 1;

const isComplimentary = (order) => {
  const mode = normalizePaymentLabel(order?.payment_mode ?? order?.paymentmode ?? order?.mode);
  return mode === "Complimentary" || order?.is_complimentary === true || Number(order?.is_complimentary) === 1;
};

const taxOf = (order) => {
  const direct = firstNumber(order, ["total_tax", "tax_total", "totaltax", "tax_amount", "tax"]);
  if (direct) return direct;
  const gst = parseObject(order?.gstdetails ?? order?.gst_details);
  const row = Array.isArray(gst) ? gst[0] : gst;
  return firstNumber(row, ["gst", "total", "tax"]) ||
    firstNumber(row, ["cgst"]) + firstNumber(row, ["sgst"]);
};

const discountOf = (order, amount) => {
  const direct = firstNumber(order, [
    "discount_amount", "discountamount", "total_discount", "discount_value",
    "loyalty_discount", "loyalty_discount_amount",
  ]);
  if (direct) return direct;
  const itemGross = orderItems(order).reduce((sum, item) => {
    const qty = firstNumber(item, ["total_quantity", "qty", "quantity", "qtyy"]);
    const lineAmount = firstNumber(item, ["total_amount", "amount"]);
    return sum + (lineAmount || qty * firstNumber(item, ["priceaftertax", "taxedprice", "unit_price", "price"]));
  }, 0);
  const delivery = firstNumber(order, ["delivery_charge", "deliverycharge"]);
  return Math.max(0, itemGross + delivery - amount);
};

const orderTypeOf = (order) => {
  const raw = String(
    order?.order_type ?? order?.ordertype ?? order?.type ?? order?.service_type ?? order?.source ?? ""
  ).toLowerCase();
  const table = String(order?.tableid ?? order?.table_id ?? order?.table_name ?? "").toLowerCase();
  if (raw.includes("delivery") || Number(order?.delivery_enabled) === 1) return "Delivery";
  if (raw.includes("room") || table.includes("room")) return "Room";
  if (raw.includes("token")) return "Token";
  if (raw.includes("qr")) return "QR Order";
  if (raw.includes("take") || ["", "0", "-1"].includes(table) || table.includes("takeaway")) return "Take Away";
  return "Dine In";
};

const add = (map, label, amount) => map.set(label, n(map.get(label)) + n(amount));

const paymentParts = (order, total) => {
  const rawSplit = parseObject(order?.split_payment ?? order?.splitPayment);
  const split = parseSplitPayment(rawSplit);
  if (split && split.total > 0) {
    return [
      ["Cash", split.cash], ["UPI", split.upi], ["Card", split.card], ["Credit", split.credit],
    ].filter(([, amount]) => amount > 0);
  }
  return [[normalizePaymentLabel(order?.payment_mode ?? order?.paymentmode ?? order?.mode), total]];
};

const BAR_WORDS = /\b(bar|beer|wine|whisky|whiskey|vodka|rum|gin|alcohol|liquor|cocktail)\b/i;

export const buildTransactionSummary = (orders = []) => {
  const payments = new Map([
    ["Cash", 0], ["UPI", 0], ["Paytm", 0], ["PhonePe", 0],
    ["Google Pay", 0], ["Card", 0], ["Credit", 0],
  ]);
  const orderTypes = new Map([
    ["Dine In", 0], ["Take Away", 0], ["Delivery", 0], ["Room", 0],
    ["Token", 0], ["QR Order", 0],
  ]);
  let totalSale = 0;
  let totalQty = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalPax = 0;
  let duePayment = 0;
  let deliveryCharges = 0;
  let cancelledOrders = 0;
  let complimentaryOrders = 0;
  let complimentaryAmount = 0;
  let foodSale = 0;
  let barSale = 0;
  let completedOrders = 0;

  (Array.isArray(orders) ? orders : []).forEach((order) => {
    if (isCancelled(order)) {
      cancelledOrders += 1;
      return;
    }
    const amount = amountOf(order);
    const complimentary = isComplimentary(order);
    completedOrders += 1;
    totalSale += amount;
    totalQty += qtyOf(order);
    totalDiscount += discountOf(order, amount);
    totalTax += taxOf(order);
    totalPax += firstNumber(order, ["pax", "total_pax", "guest_count", "guests", "covers"]);
    deliveryCharges += firstNumber(order, ["delivery_charge", "deliverycharge"]);
    add(orderTypes, orderTypeOf(order), 1);

    if (complimentary) {
      complimentaryOrders += 1;
      complimentaryAmount += amount;
    } else {
      paymentParts(order, amount).forEach(([label, value]) => add(payments, label, value));
    }

    const explicitDue = firstNumber(order, ["due_amount", "dueamount", "pending_amount", "balance_amount"]);
    duePayment += explicitDue || (normalizePaymentLabel(order?.payment_mode ?? order?.paymentmode ?? order?.mode) === "Credit" ? amount : 0);

    const items = orderItems(order);
    if (!items.length) {
      foodSale += amount;
    } else {
      let itemTotal = 0;
      let itemBar = 0;
      items.forEach((item) => {
        const qty = firstNumber(item, ["total_quantity", "qty", "quantity", "qtyy"]);
        const value = firstNumber(item, ["total_amount", "amount"]) ||
          qty * firstNumber(item, ["priceaftertax", "taxedprice", "unit_price", "price"]);
        itemTotal += value;
        const category = String(item?.category_name ?? item?.categoryName ?? item?.category ?? "");
        if (BAR_WORDS.test(category)) itemBar += value;
      });
      const ratio = itemTotal > 0 ? amount / itemTotal : 0;
      barSale += itemBar * ratio;
      foodSale += amount - itemBar * ratio;
    }
  });

  const paymentRows = Array.from(payments, ([label, value]) => ({ label, value: Number(value.toFixed(2)) }));
  const orderRows = Array.from(orderTypes, ([label, value]) => ({ label, value }));
  return {
    transactionRows: [
      ...paymentRows,
      { label: "Due Payment", value: Number(duePayment.toFixed(2)) },
      { label: "Total Discount", value: Number(totalDiscount.toFixed(2)) },
      { label: "Total Tax", value: Number(totalTax.toFixed(2)) },
      { label: "Delivery Charges", value: Number(deliveryCharges.toFixed(2)) },
      { label: "Total Pax", value: Number(totalPax.toFixed(2)), numeric: true },
      { label: "Complimentary Value", value: Number(complimentaryAmount.toFixed(2)) },
      { label: "Total Sale", value: Number(totalSale.toFixed(2)) },
      { label: "Bar Sale", value: Number(barSale.toFixed(2)) },
      { label: "Food Sale", value: Number(foodSale.toFixed(2)) },
    ],
    orderRows: [
      ...orderRows,
      { label: "Total Quantity", value: Number(totalQty.toFixed(2)) },
      { label: "Cancelled Orders", value: cancelledOrders },
      { label: "Complimentary Orders", value: complimentaryOrders },
      { label: "Completed Orders", value: completedOrders },
      { label: "Total Orders", value: completedOrders + cancelledOrders },
    ],
    totalSale: Number(totalSale.toFixed(2)),
    totalOrders: completedOrders + cancelledOrders,
    completedOrders,
    cancelledOrders,
    totalQty: Number(totalQty.toFixed(2)),
    totalPax: Number(totalPax.toFixed(2)),
  };
};

const center = (text, width) => {
  const value = String(text || "").trim();
  if (value.length >= width) return value.slice(0, width);
  return `${" ".repeat(Math.floor((width - value.length) / 2))}${value}`;
};
const wrap = (text, width) => {
  let remaining = String(text || "").trim();
  const output = [];
  while (remaining.length > width) {
    const candidate = remaining.slice(0, width + 1);
    const breakAt = candidate.lastIndexOf(" ");
    const length = breakAt > 0 ? breakAt : width;
    output.push(remaining.slice(0, length));
    remaining = remaining.slice(length).trimStart();
  }
  if (remaining) output.push(remaining);
  return output;
};
const fmt = (value) => Number.isInteger(n(value)) ? String(n(value)) : n(value).toFixed(2);
const dateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const hour = d.getHours() % 12 || 12;
  return `${day}-${month}-${d.getFullYear()} ${String(hour).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() >= 12 ? "PM" : "AM"}`;
};

export const buildTransactionSummaryReceiptText = ({
  restaurant = "Bill Sarthi", periodLines = [], printedAt = new Date(), summary,
} = {}, lineWidth = 32) => {
  const width = Math.max(24, Number(lineWidth) || 32);
  const divider = "-".repeat(width);
  const valueWidth = width >= 48 ? 14 : 10;
  const labelWidth = width - valueWidth - 1;
  const row = (label, value) => `${String(label || "").slice(0, labelWidth).padEnd(labelWidth)} ${fmt(value).padStart(valueWidth)}`;
  const centered = (text) => wrap(text, width).forEach((line) => lines.push(center(line, width)));
  const lines = [];
  centered(restaurant);
  lines.push("");
  centered("Transaction Summary");
  periodLines.filter(Boolean).forEach(centered);
  centered(`Printed at: ${dateTime(printedAt)}`);
  const section = (title, rows, valueLabel) => {
    lines.push(divider);
    centered(title);
    lines.push(divider, row("Particular", valueLabel));
    rows.forEach((item) => lines.push(row(item.label, item.value)));
  };
  section("Transaction Details", summary?.transactionRows || [], "Amount");
  section("Order Details", summary?.orderRows || [], "Orders");
  lines.push(divider);
  return lines.join("\n");
};
